/**
 * @fileoverview Custom hook for biometric/passkey authentication
 */

import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotify } from '../../../context/NotificationContext';
import { addRecentUser, updateUserPasskeyStatus } from '../utils/recentUsers';
import { isWebAuthnSupported } from '../../../utils/webauthn';

/**
 * Custom hook for managing biometric/passkey authentication
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Callback on successful authentication
 * @param {Function} options.onError - Callback for error handling
 * @param {Function} options.setRecentUsers - Function to update recent users state
 * @returns {Object} Biometric auth state and handlers
 */
export const useBiometricAuth = ({ onSuccess, onError, setRecentUsers }) => {
  const [quickLoginLoading, setQuickLoginLoading] = useState(false);
  const [quickLoginUser, setQuickLoginUser] = useState(null);
  const { error } = useNotify();
  const { authenticateWithBiometric, getCookie, APP_URL } = useAuth();

  /**
   * Handle quick passkey login for recent users
   * @param {Object} recentUser - Recent user object
   */
  const handleQuickPasskeyLogin = async (recentUser) => {
    setQuickLoginLoading(true);
    setQuickLoginUser(recentUser);

    try {
      const result = await authenticateWithBiometric(recentUser.username);

      if (result.success) {
        // Update recent users with hasPasskey flag
        addRecentUser({
          username: recentUser.username,
          firstName: result.user?.first_name || recentUser.firstName,
          lastName: result.user?.last_name || recentUser.lastName,
          hasPasskey: true
        });

        // Reset loading state before showing success
        setQuickLoginLoading(false);
        setQuickLoginUser(null);

        onSuccess({
          username: recentUser.username,
          user: result.user,
          isQuickLogin: true
        });
      } else {
        // If passkey login fails due to no credentials or not available, update the user
        if (result.error?.includes('No credentials') ||
            result.error?.includes('not found') ||
            result.error?.includes('not available')) {
          const updatedRecent = updateUserPasskeyStatus(recentUser.username, false);
          setRecentUsers(updatedRecent);
          error('Passkey login not available. Please sign in with password.');
        } else if (result.error?.toLowerCase().includes('locked') ||
                   result.error?.toLowerCase().includes('deactivated')) {
          // Pass to error handler for modal display
          onError(result.error, true);
        } else {
          error(result.error || 'Passkey login failed. Please try with password.');
        }
        setQuickLoginLoading(false);
        setQuickLoginUser(null);
      }
    } catch (err) {
      if (err.message?.toLowerCase().includes('locked') ||
          err.message?.toLowerCase().includes('deactivated')) {
        onError(err.message, true);
      } else {
        error(err.message || 'Failed to authenticate. Please try with password.');
      }
      setQuickLoginLoading(false);
      setQuickLoginUser(null);
    }
  };

  /**
   * Handle selecting a recent user (pre-fill username or quick login)
   * @param {Object} recentUser - Recent user object
   * @param {Function} setFieldValue - Formik setFieldValue function
   */
  const handleSelectRecentUser = (recentUser, setFieldValue) => {
    if (recentUser.hasPasskey && isWebAuthnSupported()) {
      handleQuickPasskeyLogin(recentUser);
    } else {
      setFieldValue('username', recentUser.username);
    }
  };

  /**
   * Dismiss passkey prompt on server
   */
  const dismissPasskeyPrompt = async () => {
    try {
      const csrfToken = getCookie('csrf_access_token');
      const response = await fetch(`${APP_URL}/user`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({ action: 'dismiss_passkey_prompt' })
      });
      await response.json();
    } catch (err) {
      console.error('Failed to dismiss passkey prompt:', err);
    }
  };

  return {
    quickLoginLoading,
    quickLoginUser,
    handleQuickPasskeyLogin,
    handleSelectRecentUser,
    dismissPasskeyPrompt
  };
};

export default useBiometricAuth;
